import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * Register custom Tailwind theme keys so `cn()` merges conflicting utilities (tailwind-merge v3).
 * - `tracking-brand` vs `tracking-tight`
 * - `font-brand` vs `font-sans` / `font-serif` / `font-mono`
 * @see tailwind.config.js (fontFamily.brand, letterSpacing.brand)
 */
const twMerge = extendTailwindMerge({
    extend: {
        theme: {
            font: ['brand'],
            tracking: ['brand'],
        },
    },
})

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function deepEqual(obj1: any, obj2: any) {
    // 1. Base case: Primitive types and same reference
    if (obj1 === obj2) {
        return true
    }

    // 2. Handle non-objects or null
    if (
        typeof obj1 !== 'object' ||
        obj1 === null ||
        typeof obj2 !== 'object' ||
        obj2 === null
    ) {
        return false
    }

    // 3. Handle Arrays
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) {
            return false
        }
        for (let i = 0; i < obj1.length; i++) {
            if (!deepEqual(obj1[i], obj2[i])) {
                return false
            }
        }
        return true
    }

    // 4. Handle Objects
    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)

    if (keys1.length !== keys2.length) {
        return false
    }

    for (const key of keys1) {
        if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
            return false
        }
    }

    return true
}

/**
 * Strict URL validation using the URL constructor.
 * Returns true only for valid, complete URLs.
 */
export function isValidUrl(url: string): boolean {
    if (!url.trim()) return false
    try {
        new URL(url)
        return true
    } catch {
        return false
    }
}

export function parseOptionalDate(
    value: Date | string | null | undefined,
): Date | null {
    if (value == null) return null
    const d = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(d.getTime())) return null
    return d
}
